import {useCallback, useEffect, useRef, useState} from 'react';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {useComponentIsMounted} from '@netx/core-components/hooks/useComponentIsMounted';

export const useSimulateProgress = (increase = 0.25, time?: number) => {
	const [simulateProgress, setSimulateProgress] = useState(false);
	const [progress, setProgress] = useState<number>(0);

	const start = useRef<number>();
	const previousTimeStamp = useRef<number>();

	const {act} = useComponentIsMounted();

	const toggle = useCallback(
		(on: boolean) => () => setSimulateProgress(on),
		[],
	);

	// Simulate loading progress
	const _draw = useConstCallback((timestamp: number) => {
		act(() => {
			if (!simulateProgress) return;
			if (start.current == null) start.current = timestamp;

			const elapsed = timestamp - start.current;

			if (time && elapsed < time) {
				previousTimeStamp.current = timestamp;
				requestAnimationFrame(_draw);
			}

			if (time && elapsed >= time) {
				setProgress(100);
			} else if (time) {
				setProgress(Math.min((elapsed / time) * 100));
			} else {
				setProgress((progress) => Math.min((progress || 0) + increase, 100));
			}
		});
	});

	const restart = useConstCallback(() => {
		const wasActive = simulateProgress;
		start.current = undefined;
		previousTimeStamp.current = undefined;
		setProgress(0);
		!wasActive ? setSimulateProgress(true) : requestAnimationFrame(_draw);
	});

	useEffect(() => {
		if (!simulateProgress) return;
		requestAnimationFrame(_draw);
	}, [simulateProgress]);

	return {
		active: simulateProgress,
		restart,
		start: toggle(true),
		stop: toggle(false),
		progress,
	};
};

export default useSimulateProgress;
